import './SearchResult.scss'

import { Col, Row } from 'react-bootstrap'
import { useHistory } from 'react-router-dom'
import parse from 'html-react-parser'
import cn from 'classnames'

import { LMSKeys, practices } from 'components/common/api/constants'
import { navigateTo } from 'components/common/navigation'

import RembleAvatar from '../../../assets/images/Remble_Logomark-White.png'
import { LMS_TYPE_TOPIC } from 'components/common/api/Courses'
import { buildRequestURL } from 'components/common/api'

// Merge the LMS keys and practices into a single object
const _combinedKeys = { ...LMSKeys }

// Since practices is an array, we need to loop through it and merge each practice into the combined keys
practices.forEach(practice => {
  _combinedKeys[practice.lmsType] = _combinedKeys[practice.lmsType]
    ? { ..._combinedKeys[practice.lmsType], ...practice }
    : practice
})

/**
 * Search result component
 * @param {Object} params The component parameters
 * @param {Object} params.result The search result object
 * @param {Number} params.index The index of the search result
 * @param {Function} params.hideHandler The function to call when the search result should be hidden
 */
export default function SearchResult({
  result,
  index,
  term,
  hideHandler = null,
  returnPath = null
}) {
  const history = useHistory()

  // Get the title
  let title = result.title?.rendered || result.title || result.post_title
  title = title ? parse(title) : null

  // Get the preview source
  const muxStreamId = result?.acf_extended?.mux_stream_id
  let previewSource = result.featured_image_src || result.featured_image_url

  // If we have a mux stream ID, use that for the preview
  if (muxStreamId)
    previewSource = `https://image.mux.com/${muxStreamId}/thumbnail.jpg?width=60`

  // Get the authors
  const authors = result.acf_extended?.author || []

  // Get the duration, if we have one
  const duration =
    result.acf_extended?.video_duration ||
    result.acf_extended?.course_duration ||
    null

  /**
   * Click handler for the search result. This will navigate the user to the appropriate page
   * @param {Object} result The search result object
   */
  const handleClick = result => {
    // Get the type of result
    const type = result.subtype || result.type

    // Get the path for the result
    let path = _combinedKeys[type]?.hasIntro
      ? `${_combinedKeys[type].path}/intro`
      : _combinedKeys[type]?.path

    // Get the ID for the result
    const id = result.id

    // If the type is a session, we need to get the course and lesson IDs
    if (type === LMS_TYPE_TOPIC) {
      // Get the course and lesson IDs
      const courseId = result.course_meta?.course_id
      const lessonId = result.course_meta?.lesson_id

      // Replace the values in the path
      path = path.replace(':courseId', courseId)
      path = path.replace(':lessonId', lessonId)
    }

    // If we don't have a path, return
    if (!path) return

    // Navigate to the result
    const url = buildRequestURL(`${path}/${id}`, { returnPath })
    navigateTo(url, history)

    // If we have a hide handler, call it
    if (hideHandler) hideHandler()
  }

  return (
    <Row
      className={cn('search-result mx-0', `result-${index}`)}
      // Only allow the user to click the result if doesn't have topics
      onClick={_ => !result.topics?.length && handleClick(result)}
    >
      <Col xs={3}>
        {/* Do we have a featured */}
        {/* Only show the preview if we have a mux playback ID */}
        {previewSource ? (
          <img
            width={60}
            loading='lazy'
            className='search-preview-image'
            alt={title}
            src={previewSource}
            onClick={_ => handleClick(result)}
          />
        ) : (
          <div className='search-preview-image align-center text-center'>
            <img
              src={RembleAvatar}
              alt=''
              width='15'
              onClick={_ => handleClick(result)}
            />
          </div>
        )}
      </Col>
      <Col xs={9}>
        <strong className='d-block' onClick={_ => handleClick(result)}>
          {title} {/* If we have a video duration, show it */}
          {duration && <small className='muted'>({duration})</small>}
        </strong>
        {/* Do we have author info? */}
        {authors.map((author, index) => (
          <span key={index} className='d-block mt-0'>
            <small className='muted'>{author.post_title}</small>
          </span>
        ))}

        <em className='muted'>
          {LMSKeys[result.subtype || result.post_type || result.type]?.title}
        </em>

        {/* Do we have topics results? */}
        {result.topics?.length > 0 && (
          <>
            <p className='mt-2 mb-2'>
              <em>
                {result.topics.length} Session
                {result.topics.length !== 1 && 's'} matching{' '}
                <span className='green'>{term}</span>:
              </em>
            </p>
            {result.topics.map((topic, index) => (
              <div
                key={index}
                className={`session-result session-result-${index}`}
                onClick={_ => handleClick(topic)}
              >
                {topic.title}
              </div>
            ))}
          </>
        )}
      </Col>
    </Row>
  )
}
